/* @img-migrated */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, RefreshCw, Eye, Calendar, X, Image as ImageIcon, Video,
  Loader2, Upload, ExternalLink, BarChart3, DollarSign, Sparkles, ChevronDown,
  ChevronUp, Link as LinkIcon, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEMENTS = [
  { id: 'top',    label: 'Top Sidebar',         hint: '300×600 vertical · desktop right column · responsive on mobile' },
  { id: 'mid',    label: 'Mid-Feed',            hint: '728×90 or 600×300 · between news cards' },
  { id: 'bottom', label: 'Bottom Spotlight',    hint: '800×400 or carousel · before footer area' },
];

const blankAd = () => ({
  name: '', company_name: '', placement: 'top',
  media: [], carousel: false,
  slide_duration: 8,
  transition: 'crossfade',
  device_visibility: 'both',
  cta_text: 'Learn more', cta_url: '',
  start_at: '', end_at: '',
  active: false, notes: '', price: '',
  show_in_community: false,
});

const SLIDE_DURATIONS = [5, 8, 10, 12, 15];
const TRANSITIONS_OPTIONS = [
  { id: 'fade',      label: 'Fade',       hint: 'Simple opacity fade' },
  { id: 'crossfade', label: 'Crossfade',  hint: 'Soft scale + fade · default' },
  { id: 'slide',     label: 'Soft Slide', hint: 'Gentle horizontal slide' },
];

const DEVICE_OPTIONS = [
  { id: 'desktop', label: 'Desktop only', hint: 'Hidden on mobile (< 1024 px)' },
  { id: 'mobile',  label: 'Mobile only',  hint: 'Hidden on desktop (≥ 1024 px)' },
  { id: 'both',    label: 'Both',         hint: 'Default — appears on every screen' },
];

const STATUS_PILL = {
  draft:     'bg-amber-100 text-amber-700 border-amber-200',
  scheduled: 'bg-sky-100 text-sky-700 border-sky-200',
  active:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired:   'bg-slate-100 text-slate-500 border-slate-200',
  paused:    'bg-violet-100 text-violet-700 border-violet-200',
};

const AdminNewsAdsPage = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');

  // Lock body scroll while editor modal is open
  useBodyScrollLock(editorOpen);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/news-ads`, { withCredentials: true });
      setAds(res.data.ads || []);
    } catch (err) {
      toast.error('Could not load ads');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return ads;
    return ads.filter((a) => a.status === filter || a.placement === filter);
  }, [ads, filter]);

  const openNew = () => { setEditing(blankAd()); setEditorOpen(true); };
  const openEdit = (ad) => {
    setEditing({
      ...ad,
      start_at: ad.start_at ? ad.start_at.slice(0, 16) : '',
      end_at: ad.end_at ? ad.end_at.slice(0, 16) : '',
    });
    setEditorOpen(true);
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete "${ad.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/news-ads/${ad.id}`, { withCredentials: true });
      toast.success('Ad deleted');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const toggleActive = async (ad) => {
    try {
      await axios.put(`${API}/admin/news-ads/${ad.id}`, { active: !ad.active }, { withCredentials: true });
      toast.success(!ad.active ? 'Ad activated' : 'Ad paused');
      fetchAll();
    } catch (err) { toast.error('Failed to update'); }
  };

  // Analytics summary
  const summary = useMemo(() => {
    const acc = { total: ads.length, active: 0, drafts: 0, expired: 0, views: 0, clicks: 0, revenue: 0 };
    for (const a of ads) {
      if (a.status === 'active')  acc.active++;
      if (a.status === 'draft')   acc.drafts++;
      if (a.status === 'expired') acc.expired++;
      acc.views  += (a.views  || 0);
      acc.clicks += (a.clicks || 0);
      acc.revenue += parseFloat(a.price || 0) || 0;
    }
    return acc;
  }, [ads]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid="admin-news-ads-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#002249] text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#002249] tracking-tight">News & Insights Ads</h1>
              <p className="text-[13px] text-slate-500 mt-0.5">Premium sponsored placements — only visible on the public News page.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchAll} disabled={loading} className="gap-1.5 h-10" data-testid="ads-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button onClick={openNew} className="gap-1.5 h-10 bg-[#E63946] hover:bg-[#c02a35]" data-testid="ads-new">
              <Plus className="w-4 h-4" />New Ad
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <SummaryTile label="Active" value={summary.active} accent="#10b981" Icon={Sparkles} />
          <SummaryTile label="Drafts" value={summary.drafts} accent="#f59e0b" Icon={Edit} />
          <SummaryTile label="Total Views" value={summary.views.toLocaleString()} accent="#0ea5e9" Icon={Eye} />
          <SummaryTile label="Total Clicks" value={summary.clicks.toLocaleString()} accent="#E63946" Icon={BarChart3} />
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
          {['all', 'active', 'draft', 'expired', 'top', 'mid', 'bottom'].map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-shrink-0 h-8 px-3 rounded-full text-[12px] font-semibold border transition-all ${
                  active ? 'bg-[#002249] text-white border-[#002249]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`ads-filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 py-20 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center px-6" data-testid="ads-empty">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-[15px] font-bold text-slate-800">No ads {filter !== 'all' ? `match "${filter}"` : 'yet'}</p>
            <p className="text-[13px] text-slate-500 mt-1">Create your first sponsored placement to start monetizing News & Insights.</p>
            <Button onClick={openNew} className="mt-5 gap-1.5 bg-[#E63946] hover:bg-[#c02a35]"><Plus className="w-4 h-4" />New Ad</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ads-list">
            {filtered.map((ad) => (
              <AdRow key={ad.id} ad={ad} onEdit={openEdit} onDelete={remove} onToggle={toggleActive} />
            ))}
          </div>
        )}
      </div>

      {/* Editor */}
      <AnimatePresence>
        {editorOpen && editing && (
          <AdEditor
            value={editing}
            onClose={() => { setEditorOpen(false); setEditing(null); }}
            onSaved={() => { setEditorOpen(false); setEditing(null); fetchAll(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const SummaryTile = ({ label, value, accent, Icon }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3.5 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: accent }}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-[16px] font-bold text-[#002249] leading-tight mt-0.5">{value}</p>
    </div>
  </div>
);

const AdRow = ({ ad, onEdit, onDelete, onToggle }) => {
  const placement = PLACEMENTS.find((p) => p.id === ad.placement);
  const cover = (ad.media || [])[0];
  const ctr = ad.views ? ((ad.clicks / ad.views) * 100).toFixed(1) : '0.0';
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`ad-row-${ad.id}`}>
      <div className="flex">
        <div className="w-[140px] sm:w-[180px] aspect-square bg-slate-100 flex-shrink-0 overflow-hidden relative">
          {cover ? (
            cover.kind === 'video' ? (
              <video src={resolveImg(cover.url)} muted className="w-full h-full object-cover" />
            ) : (
              <Img variant="generic" src={resolveImg(cover.url)} alt={ad.name} className="w-full h-full object-cover" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-8 h-8" /></div>
          )}
          {(ad.media || []).length > 1 && (
            <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-black/60 text-white">
              {ad.media.length} media
            </span>
          )}
        </div>
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider border ${STATUS_PILL[ad.status] || STATUS_PILL.draft}`}>
              {ad.status}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
              {placement?.label || ad.placement}
            </span>
          </div>
          <p className="text-[14.5px] font-bold text-[#002249] mt-2 truncate" title={ad.name}>{ad.name}</p>
          <p className="text-[12px] text-slate-500 truncate">{ad.company_name || '—'}</p>

          <div className="grid grid-cols-3 gap-2 mt-3 text-[10.5px] text-slate-500">
            <div><span className="text-slate-400 block">Views</span><span className="text-[#002249] font-semibold text-[12.5px]">{(ad.views || 0).toLocaleString()}</span></div>
            <div><span className="text-slate-400 block">Clicks</span><span className="text-[#002249] font-semibold text-[12.5px]">{(ad.clicks || 0).toLocaleString()}</span></div>
            <div><span className="text-slate-400 block">CTR</span><span className="text-[#002249] font-semibold text-[12.5px]">{ctr}%</span></div>
          </div>

          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            <Button onClick={() => onToggle(ad)} size="sm" variant="outline" className={`h-7 text-[11px] gap-1 ${ad.active ? 'border-emerald-200 text-emerald-700' : 'border-slate-200'}`} data-testid={`ad-toggle-${ad.id}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${ad.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              {ad.active ? 'Active' : 'Paused'}
            </Button>
            <Button onClick={() => onEdit(ad)} size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`ad-edit-${ad.id}`}>
              <Edit className="w-3 h-3" />Edit
            </Button>
            <Button onClick={() => onDelete(ad)} size="sm" variant="outline" className="h-7 text-[11px] border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`ad-delete-${ad.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- Editor (modal) -------------------- */
const AdEditor = ({ value, onClose, onSaved }) => {
  const [form, setForm] = useState(value);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const next = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/admin/news-ads/upload-media`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        });
        next.push({ kind: res.data.kind, url: res.data.url });
      }
      set({ media: [...(form.media || []), ...next] });
      toast.success(`Uploaded ${next.length} file(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeMedia = (i) => {
    set({ media: form.media.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Ad name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        carousel: (form.media || []).length > 1 && form.carousel,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at:   form.end_at   ? new Date(form.end_at).toISOString()   : null,
        price: form.price ? parseFloat(form.price) : null,
      };
      if (form.id) {
        await axios.put(`${API}/admin/news-ads/${form.id}`, payload, { withCredentials: true });
        toast.success('Ad updated');
      } else {
        await axios.post(`${API}/admin/news-ads`, payload, { withCredentials: true });
        toast.success('Ad created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Compute the status preview based on current form values
  const statusPreview = useMemo(() => {
    const media = form.media || [];
    if (!media.length) return 'draft';
    const now = new Date();
    const end = form.end_at ? new Date(form.end_at) : null;
    if (end && end < now) return 'expired';
    if (!form.active) return 'paused';
    const start = form.start_at ? new Date(form.start_at) : null;
    if (start && start > now) return 'scheduled';
    return 'active';
  }, [form.media, form.start_at, form.end_at, form.active]);

  const placement = PLACEMENTS.find((p) => p.id === form.placement);

  return (
    <ModalPortal>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
      >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-[760px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
        data-testid="ad-editor"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-[15.5px] font-bold text-[#002249]">{form.id ? 'Edit Ad' : 'New Ad'}</h3>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Premium sponsored placement on News & Insights only.</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-6">
          {/* ─────────── 1. BASIC INFORMATION ─────────── */}
          <Section number="1" title="Basic Information" testId="ad-section-basic">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Ad Name</Label>
                <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Summer Hospitality Spotlight" data-testid="ad-name" />
              </div>
              <div>
                <Label className="text-[12px]">Sponsored Company</Label>
                <Input value={form.company_name} onChange={(e) => set({ company_name: e.target.value })} placeholder="Company name" data-testid="ad-company" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[12px]">Placement</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                {PLACEMENTS.map((p) => {
                  const active = form.placement === p.id;
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => set({ placement: p.id })}
                      className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${
                        active ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`ad-placement-${p.id}`}
                    >
                      <p className={`text-[12.5px] font-bold ${active ? 'text-[#002249]' : 'text-slate-700'}`}>{p.label}</p>
                      <p className="text-[10.5px] text-slate-500 mt-0.5">{p.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          {/* ─────────── 2. MEDIA ─────────── */}
          <Section number="2" title="Media · per-slide timing & links" testId="ad-section-media" hint="Each slide can override the global default duration. Videos always play to completion.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ad-media-list">
              {(form.media || []).map((m, i) => (
                <MediaSlideCard
                  key={`${m.url}-${i}`}
                  index={i}
                  total={(form.media || []).length}
                  slide={m}
                  defaultDuration={form.slide_duration}
                  onChange={(patch) => {
                    const updated = [...(form.media || [])];
                    updated[i] = { ...updated[i], ...patch };
                    set({ media: updated });
                  }}
                  onRemove={() => removeMedia(i)}
                  onMove={(dir) => {
                    const list = [...(form.media || [])];
                    const j = i + dir;
                    if (j < 0 || j >= list.length) return;
                    [list[i], list[j]] = [list[j], list[i]];
                    set({ media: list });
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="min-h-[180px] rounded-xl border-2 border-dashed border-slate-300 hover:border-[#002249] hover:bg-[#002249]/5 flex flex-col items-center justify-center text-slate-500 hover:text-[#002249] transition-colors p-4"
                data-testid="ad-upload-btn"
              >
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                <span className="text-[12.5px] font-semibold mt-1.5">{uploading ? 'Uploading…' : 'Add image / video'}</span>
                <span className="text-[10.5px] text-slate-400 mt-0.5">JPG · PNG · WEBP · MP4 · WEBM · up to 30 MB</span>
              </button>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="ad-file-input"
            />
            {(form.media || []).length > 1 && (
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer font-semibold">
                  <input type="checkbox" checked={!!form.carousel} onChange={(e) => set({ carousel: e.target.checked })} data-testid="ad-carousel-toggle" />
                  Show as carousel (multiple media will rotate)
                </label>
                <p className="text-[10.5px] text-slate-500 pl-6">
                  Default duration applies only if no custom duration is set on the slide. Videos always play to completion.
                </p>
              </div>
            )}
          </Section>

          {/* ─────────── 3. CTA ─────────── */}
          <Section number="3" title="Call to Action" testId="ad-section-cta" hint="Optional — leave blank for media-only display.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">CTA Text</Label>
                <Input value={form.cta_text} onChange={(e) => set({ cta_text: e.target.value })} placeholder="Learn more" data-testid="ad-cta-text" />
              </div>
              <div>
                <Label className="text-[12px]">CTA Link</Label>
                <Input value={form.cta_url} onChange={(e) => set({ cta_url: e.target.value })} placeholder="https://…" data-testid="ad-cta-url" />
              </div>
            </div>
          </Section>

          {/* ─────────── 4. AD SCHEDULE & CAROUSEL TIMING ─────────── */}
          <Section number="4" title="Ad Schedule & Carousel Timing" testId="ad-section-schedule">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px]">Start Date / Time</Label>
                <Input type="datetime-local" value={form.start_at} onChange={(e) => set({ start_at: e.target.value })} data-testid="ad-start" />
                <p className="text-[10.5px] text-slate-400 mt-1">Leave empty to start immediately.</p>
              </div>
              <div>
                <Label className="text-[12px]">End Date / Time</Label>
                <Input type="datetime-local" value={form.end_at} onChange={(e) => set({ end_at: e.target.value })} data-testid="ad-end" />
                <p className="text-[10.5px] text-slate-400 mt-1">Leave empty for an open-ended ad.</p>
              </div>
            </div>

            <div className="mt-3 flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <input
                id="ad-active-toggle" type="checkbox" checked={!!form.active}
                onChange={(e) => set({ active: e.target.checked })}
                data-testid="ad-active-toggle" className="mt-0.5"
              />
              <label htmlFor="ad-active-toggle" className="cursor-pointer flex-1">
                <p className="text-[13px] font-bold text-slate-800">Active</p>
                <p className="text-[11px] text-slate-500">Manually enable or pause this ad regardless of schedule.</p>
              </label>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_PILL[statusPreview]}`} data-testid="ad-status-preview">
                {statusPreview}
              </span>
            </div>

            <div className="mt-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                id="ad-community-toggle" type="checkbox" checked={!!form.show_in_community}
                onChange={(e) => set({ show_in_community: e.target.checked })}
                data-testid="ad-community-toggle" className="mt-0.5"
              />
              <label htmlFor="ad-community-toggle" className="cursor-pointer flex-1">
                <p className="text-[13px] font-bold text-slate-800">Show in Community Feed</p>
                <p className="text-[11px] text-slate-600">Native ad appears every 6 posts inside the Member Portal Community feed.</p>
              </label>
            </div>

            {/* Device Visibility — segmented control */}
            <div className="mt-4 p-3 border border-slate-200 rounded-lg bg-white" data-testid="ad-section-device">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[12.5px] font-bold text-[#002249]">Device Visibility</p>
                <span className="text-[10.5px] text-slate-400">Where this ad is allowed to render</span>
              </div>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Device visibility">
                {DEVICE_OPTIONS.map((opt) => {
                  const active = (form.device_visibility || 'both') === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => set({ device_visibility: opt.id })}
                      className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${
                        active ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`ad-device-${opt.id}`}
                    >
                      <p className={`text-[12.5px] font-bold ${active ? 'text-[#002249]' : 'text-slate-700'}`}>{opt.label}</p>
                      <p className="text-[10.5px] text-slate-500 mt-0.5">{opt.hint}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10.5px] text-slate-500 mt-2 inline-flex items-center gap-1.5" data-testid="ad-device-summary">
                {form.device_visibility === 'desktop' && (<span><span className="text-emerald-600 font-semibold">✓ Visible on desktop</span> · <span className="text-rose-500">✗ Hidden on mobile</span></span>)}
                {form.device_visibility === 'mobile' && (<span><span className="text-rose-500">✗ Hidden on desktop</span> · <span className="text-emerald-600 font-semibold">✓ Visible on mobile</span></span>)}
                {(!form.device_visibility || form.device_visibility === 'both') && (<span className="text-emerald-600 font-semibold">✓ Visible on all devices</span>)}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-[12px]">Carousel Slide Duration</Label>
                <select
                  value={form.slide_duration}
                  onChange={(e) => set({ slide_duration: parseInt(e.target.value) })}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                  data-testid="ad-slide-duration"
                >
                  {SLIDE_DURATIONS.map((s) => (
                    <option key={s} value={s}>{s} seconds</option>
                  ))}
                </select>
                <p className="text-[10.5px] text-slate-400 mt-1">Default for image slides without a custom duration. Videos always play to completion.</p>
              </div>
              <div>
                <Label className="text-[12px]">Transition Style</Label>
                <select
                  value={form.transition}
                  onChange={(e) => set({ transition: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                  data-testid="ad-transition"
                >
                  {TRANSITIONS_OPTIONS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label} — {t.hint}</option>
                  ))}
                </select>
                <p className="text-[10.5px] text-slate-400 mt-1">Animation between slides. Crossfade is the default.</p>
              </div>
            </div>
          </Section>

          {/* ─────────── 5. INTERNAL TRACKING ─────────── */}
          <Section number="5" title="Internal Tracking" testId="ad-section-tracking" hint="Visible only to admins.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />Internal price</Label>
                <Input type="number" step="0.01" value={form.price || ''} onChange={(e) => set({ price: e.target.value })} placeholder="0.00" data-testid="ad-price" />
              </div>
              <div>
                <Label className="text-[12px]">Notes</Label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                  placeholder="Internal notes, contract details, etc."
                  data-testid="ad-notes"
                />
              </div>
            </div>
          </Section>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#c02a35] gap-1.5" data-testid="ad-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {form.id ? 'Save Changes' : 'Create Ad'}
          </Button>
        </div>
      </motion.div>
      </motion.div>
    </ModalPortal>
  );
};

/* Per-slide editor card — thumbnail, type chip, duration override, link, reorder, remove. */
const MediaSlideCard = ({ index, total, slide, defaultDuration, onChange, onRemove, onMove }) => {
  const isVideo = slide.kind === 'video';
  const usingDefault = !slide.duration;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col" data-testid={`ad-media-slide-${index}`}>
      <div className="relative aspect-[16/9] bg-slate-100">
        {isVideo ? (
          <video src={resolveImg(slide.url)} muted playsInline className="w-full h-full object-cover" />
        ) : (
          <Img variant="generic" src={resolveImg(slide.url)} alt="" className="w-full h-full object-cover" />
        )}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider bg-black/65 text-white backdrop-blur-sm">
          {isVideo ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
          {slide.kind}
        </span>
        <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider bg-white/90 text-slate-700 border border-slate-200">
          Slide {index + 1} / {total}
        </span>
      </div>

      <div className="p-3 space-y-2.5 flex-1 flex flex-col">
        {/* Duration */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[11.5px] font-bold text-slate-700 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> Duration
            </Label>
            {isVideo && (
              <span className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wider">Plays full length</span>
            )}
          </div>
          <select
            value={slide.duration || ''}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ duration: v ? parseInt(v) : undefined });
            }}
            disabled={isVideo}
            className="mt-1 w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20 disabled:bg-slate-50 disabled:text-slate-400"
            data-testid={`ad-media-duration-${index}`}
          >
            <option value="">Use default ({defaultDuration || 8}s)</option>
            <option value="5">5 seconds</option>
            <option value="8">8 seconds</option>
            <option value="10">10 seconds</option>
            <option value="12">12 seconds</option>
            <option value="15">15 seconds</option>
          </select>
          {!isVideo && usingDefault && (
            <p className="text-[10px] text-slate-400 mt-1">Default duration applies only if no custom duration is set.</p>
          )}
        </div>

        {/* Per-slide link */}
        <div>
          <Label className="text-[11.5px] font-bold text-slate-700 inline-flex items-center gap-1">
            <LinkIcon className="w-3 h-3" /> Slide link <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Input
            value={slide.link || ''}
            onChange={(e) => onChange({ link: e.target.value })}
            placeholder="Falls back to ad CTA link"
            className="h-9 text-[12.5px] mt-1"
            data-testid={`ad-media-link-${index}`}
          />
        </div>

        {/* Action row */}
        <div className="flex items-center justify-between pt-1 mt-auto">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onMove(-1)}
              disabled={index === 0}
              className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[#002249] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              aria-label="Move up"
              data-testid={`ad-media-up-${index}`}
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
              className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[#002249] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              aria-label="Move down"
              data-testid={`ad-media-down-${index}`}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200"
            data-testid={`ad-media-remove-${index}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
};

/* Section wrapper — visual grouping inside the editor */
const Section = ({ number, title, hint, testId, children }) => (
  <div className="space-y-2.5" data-testid={testId}>
    <div className="flex items-baseline gap-2">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#002249] text-white text-[10px] font-bold flex-shrink-0">{number}</span>
      <h4 className="text-[13.5px] font-bold text-[#002249] tracking-tight">{title}</h4>
      {hint && <span className="text-[11px] text-slate-400 font-normal">— {hint}</span>}
    </div>
    <div>{children}</div>
  </div>
);

export default AdminNewsAdsPage;
